import OriginTracer from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';
import { useInvisibleRecaptcha } from '@atlassian/google-recaptcha';

import { AnalyticsActionSubject } from '../components/analytics';
import type { Product } from '../types/product';
import { postJson } from '../utils/fetch';
import { getProductAris } from '../utils/get-product-aris';
import { getRecaptchaSiteKey } from '../utils/recaptcha';

type InviteUser = {
	email: string;
	id?: string;
};

type InviteRequest = {
	users: InviteUser[];
	continueUrl: string;
	resources: string[];
	suppressInviteEmail: boolean;
	roleName: string;
	recaptchaToken?: string;
};

type ErrorReason = {
	message: string;
	description: string;
};

type InviteResponseItem = {
	id?: string;
	email?: string;
	results?: Record<
		string,
		'INVITED' | 'USER_EXISTS' | 'INVITED_PENDING_APPROVAL' | 'PENDING_INVITE_EXISTS' | 'NOT_INVITED'
	>;
	errorReasons?: Record<string, ErrorReason>;
};

const getContinueUrl = ({ originTracer }: { originTracer: OriginTracer }) => {
	const origin = window.location.origin;

	// For product domains, preference sending to the origin location in order to give the user an "onboarding" experience.
	if (origin && (origin.endsWith('.jira-dev.com') || origin.endsWith('.atlassian.net'))) {
		return originTracer.addToUrl(origin);
	}

	return '';
};

export const useInviteToProduct = ({
	products,
	source,
	originProduct,
}: {
	products: Product[];
	source?: string;
	originProduct: string;
}) => {
	const { getRecaptchaToken, unloadRecaptchaForSite } = useInvisibleRecaptcha({
		siteKey: getRecaptchaSiteKey(),
		displayBadge: false,
		analyticsChannel: 'peopleAndTeams',
		analyticsSource: 'useInviteToProduct',
		preventUnloadRecaptchaOnCleanup: true,
	});
	const { fireEvent } = useAnalyticsEventsNext();

	return async (emails: string[], cloudId: string): Promise<InviteResponseItem[] | void> => {
		try {
			// Get reCAPTCHA token
			const recaptchaToken = await getRecaptchaToken?.('invite_to_product').catch(() => {
				// If token generation fails we want to just continue with the
				// request without a recaptcha token (the lib will fire an event)
				return undefined;
			});
			const productAris = getProductAris({ products, cloudId });

			const originTracer = new OriginTracer({ product: originProduct });
			const originAttributes = originTracer.toAnalyticsAttributes({
				hasGeneratedId: true,
			});

			const payload: InviteRequest = {
				users: emails.map((email) => ({ email })),
				continueUrl: getContinueUrl({ originTracer }),
				resources: productAris,
				suppressInviteEmail: false,
				roleName: 'product/member',
			};

			const result = await postJson<InviteResponseItem[]>(
				'/gateway/api/invitations/invite',
				payload,
				recaptchaToken ? { 'G-Recaptcha-Token': recaptchaToken } : undefined,
			);
			// Manually unload recaptcha. Could not rely on built in cleanup on unmount since it was happening too early and causing token not to be generated
			unloadRecaptchaForSite?.();

			const invites = result.filter(
				(item) =>
					item.results &&
					(Object.values(item.results).includes('INVITED') ||
						Object.values(item.results).includes('INVITED_PENDING_APPROVAL')),
			);

			invites.forEach((invite) => {
				if (invite.id) {
					// @ts-expect-error - we should never dynamically set the actionSubjectId
					fireEvent(`track.user.invited.${invite.id}`, {
						integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
						products,
						inviteResult: invite.results || {},
						inviteErrorReasons: invite.errorReasons || {},
						...originAttributes,
					});
				}
			});

			const errors = result.filter(
				(item) => item.errorReasons && Object.keys(item.errorReasons).length > 0,
			);

			if (errors.length > 0) {
				fireEvent('track.inviteToProductService.failed', {
					integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
					message: 'Errors occurred with inviting users',
					errorsCount: errors.length,
					errors,
					result,
				});
			}

			return result;
		} catch (error) {
			const errorMessage = 'Failed to invite users';
			const typeGuardedError = error instanceof Error ? error : new Error(errorMessage);

			fireEvent('track.inviteToProductService.failed', {
				integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
				message: typeGuardedError.message,
				errorsCount: null,
				errors: null,
				result: null,
			});
		}
	};
};
